import { supabase } from '../lib/supabase';
import { sanitizeInput, validateUrl } from '../utils/securityUtils';

export type LookalInternalAdStatus = 'active' | 'inactive';
export type LookalInternalAdMediaType = 'image' | 'video';
/** Internal ads are admin-only inventory, so fullscreen costs nothing — but it
 *  paces like the paid tiers: the media fills the screen, then the QR holds. */
export type LookalInternalAdLayout = 'three_zone' | 'fullscreen';

/** The two fullscreen lengths an internal ad can use, mirroring the paid tiers. */
export const INTERNAL_FULLSCREEN_DURATIONS = [15, 30] as const;
/** Three-zone internal ads keep today's single 8s pass with no QR hold. */
export const INTERNAL_THREE_ZONE_DURATION_SECONDS = 8;
export const INTERNAL_DEFAULT_FULLSCREEN_DURATION_SECONDS = 15;
export const INTERNAL_QR_HOLD_SECONDS = 3;

export interface LookalInternalAd {
  id: string;
  name: string;
  media_type: LookalInternalAdMediaType;
  image_url: string | null;
  video_url: string | null;
  preview_url: string | null;
  ad_content: Record<string, unknown>;
  selected_video: Record<string, unknown> | null;
  destination_url: string | null;
  cta: string | null;
  status?: LookalInternalAdStatus;
  priority: number;
  layout?: LookalInternalAdLayout | null;
  duration_seconds?: number | null;
  qr_tail_seconds?: number | null;
  created_at: string;
  updated_at: string;
}

export interface LookalInternalAdInput {
  id?: string;
  name: string;
  media_type: LookalInternalAdMediaType;
  image_url?: string | null;
  video_url?: string | null;
  preview_url?: string | null;
  ad_content?: Record<string, unknown>;
  selected_video?: Record<string, unknown> | null;
  destination_url?: string | null;
  cta?: string | null;
  status?: LookalInternalAdStatus;
  priority?: number;
  layout?: LookalInternalAdLayout;
  /** Only meaningful for fullscreen: 15 or 30. */
  duration_seconds?: number;
  /** Seconds the QR holds after the media. Only used for fullscreen. */
  qr_tail_seconds?: number;
}

type ServiceResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

type NormalizedLookalInternalAdInput = Omit<LookalInternalAdInput, 'id'> & {
  id?: string;
  status: LookalInternalAdStatus;
  priority: number;
  image_url: string | null;
  video_url: string | null;
  preview_url: string | null;
  destination_url: string | null;
  cta: string | null;
  ad_content: Record<string, unknown>;
  selected_video: Record<string, unknown> | null;
  layout: LookalInternalAdLayout;
  duration_seconds: number;
  qr_tail_seconds: number;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

const normalizeOptionalUrl = (
  value: string | null | undefined,
  label: string
): ServiceResponse<string | null> => {
  const sanitized = sanitizeInput(value);
  if (!sanitized) {
    return { success: true, data: null };
  }

  const validUrl = validateUrl(sanitized);
  if (!validUrl) {
    return { success: false, error: `Invalid ${label} URL` };
  }

  return { success: true, data: validUrl };
};

/**
 * True when an ad's Zone A creative is an operator-uploaded poster rather than a
 * designed canvas.
 *
 * `ad_content.contentSource === 'own'` is the explicit marker. Rows written before
 * own-content mode forced `media_type: 'image'` instead report `media_type: 'video'`
 * while carrying a poster and no video at all — the active-ads view drops those
 * outright, so they are treated as uploads too. Both the builder and the public
 * preview page read this so the two never disagree about what an ad is.
 */
export const isUploadedPosterAd = (ad: Pick<LookalInternalAd, 'media_type' | 'video_url' | 'preview_url' | 'ad_content'>): boolean => {
  const content = isPlainObject(ad.ad_content) ? ad.ad_content : {};
  if (content.contentSource === 'own') return true;
  return ad.media_type === 'video' && !ad.video_url && Boolean(ad.preview_url);
};

export const normalizeLookalInternalAdInput = (
  input: LookalInternalAdInput
): ServiceResponse<NormalizedLookalInternalAdInput> => {
  const name = sanitizeInput(input.name);
  if (!name) {
    return { success: false, error: 'Name is required' };
  }

  if (input.media_type !== 'image' && input.media_type !== 'video') {
    return { success: false, error: 'Media type must be image or video' };
  }

  const status = input.status ?? 'inactive';
  if (status !== 'active' && status !== 'inactive') {
    return { success: false, error: 'Status must be active or inactive' };
  }

  const adContent = input.ad_content ?? {};
  if (!isPlainObject(adContent)) {
    return { success: false, error: 'Ad content must be a plain object' };
  }

  const selectedVideo = input.selected_video ?? null;
  if (selectedVideo !== null && !isPlainObject(selectedVideo)) {
    return { success: false, error: 'Selected video must be a plain object' };
  }

  const priority = input.priority ?? 1;
  if (!Number.isInteger(priority)) {
    return { success: false, error: 'Priority must be an integer' };
  }
  if (priority < 0 || priority > 1000) {
    return { success: false, error: 'Priority must be between 0 and 1000' };
  }

  const urlFields = [
    ['image_url', 'image'] as const,
    ['video_url', 'video'] as const,
    ['preview_url', 'preview'] as const,
    ['destination_url', 'destination'] as const,
  ];
  const urls: Record<string, string | null> = {};

  for (const [field, label] of urlFields) {
    const result = normalizeOptionalUrl(input[field], label);
    if (!result.success) {
      return { success: false, error: result.error };
    }
    urls[field] = result.data ?? null;
  }

  // Layout decides the on-screen format. A three-zone ad is pinned to today's 8s
  // pass with no QR hold; a fullscreen ad plays 15s or 30s like the paid tiers and
  // holds the QR for 3s. Deriving the timing here keeps the two in step, so a stale
  // hold can never stretch an ad that has no overlay.
  const layout: LookalInternalAdLayout = input.layout === 'fullscreen' ? 'fullscreen' : 'three_zone';
  let durationSeconds = INTERNAL_THREE_ZONE_DURATION_SECONDS;
  let qrTailSeconds = 0;
  if (layout === 'fullscreen') {
    durationSeconds = input.duration_seconds === 30
      ? 30
      : INTERNAL_DEFAULT_FULLSCREEN_DURATION_SECONDS;
    const requestedTail = input.qr_tail_seconds;
    qrTailSeconds = Number.isInteger(requestedTail)
      ? Math.min(15, Math.max(0, requestedTail as number))
      : INTERNAL_QR_HOLD_SECONDS;
  }

  const normalized: NormalizedLookalInternalAdInput = {
    id: input.id,
    name,
    media_type: input.media_type,
    image_url: urls.image_url,
    video_url: urls.video_url,
    preview_url: urls.preview_url,
    ad_content: adContent,
    selected_video: selectedVideo,
    destination_url: urls.destination_url,
    cta: sanitizeInput(input.cta) || null,
    status,
    priority,
    layout,
    duration_seconds: durationSeconds,
    qr_tail_seconds: qrTailSeconds,
  };

  if (status === 'active') {
    const hasRenderableAdContent = Boolean(
      typeof normalized.ad_content.headline === 'string'
      && sanitizeInput(normalized.ad_content.headline)
    );

    if (normalized.media_type === 'image' && !normalized.preview_url && !normalized.image_url && !hasRenderableAdContent) {
      return { success: false, error: 'Active image ad requires an image or preview URL' };
    }

    if (normalized.media_type === 'video' && !normalized.video_url) {
      return { success: false, error: 'Active video ad requires a video URL' };
    }
  }

  return { success: true, data: normalized };
};

class LookalInternalAdService {
  async listActive(): Promise<ServiceResponse<LookalInternalAd[]>> {
    try {
      const { data, error } = await supabase
        .from('active_lookal_internal_ads')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) return { success: false, error: error.message };
      return { success: true, data: data ?? [] };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch active internal ads' };
    }
  }

  async getById(id: string): Promise<ServiceResponse<LookalInternalAd | null>> {
    try {
      const { data, error } = await supabase
        .from('active_lookal_internal_ads')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) return { success: false, error: error.message };
      return { success: true, data: data ?? null };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch internal ad' };
    }
  }

  async getActive(): Promise<ServiceResponse<LookalInternalAd | null>> {
    try {
      const result = await this.listActive();
      if (!result.success) return { success: false, error: result.error };
      return { success: true, data: result.data?.[0] ?? null };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch active internal ad',
      };
    }
  }

  async list(): Promise<ServiceResponse<LookalInternalAd[]>> {
    try {
      const { data, error } = await supabase
        .from('lookal_internal_ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data ?? [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to list internal ads',
      };
    }
  }

  async save(input: LookalInternalAdInput): Promise<ServiceResponse<LookalInternalAd>> {
    try {
      const normalized = normalizeLookalInternalAdInput(input);
      if (!normalized.success || !normalized.data) {
        return { success: false, error: normalized.error };
      }

      const { data, error } = await supabase.rpc('save_lookal_internal_ad', {
        p_ad: normalized.data,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save internal ad',
      };
    }
  }
}

export const lookalInternalAdService = new LookalInternalAdService();
